import {AfterContentInit, Component, EventEmitter, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {combineLatest, Observable, of, Subscription}                             from 'rxjs';
import {ApGridPagerType, IGridPagerData}                                         from '../../ap-interface';
import {
  ApDynComponentComponent
}                                                                                from '../../ap-dyncomponent/ap-dyncomponent.component';
import {
  ApDynGridColumnConfigBase
}                                                                                from '../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {
  ApDynGridPagerConfigBase
}                                                                                from '../../ap-dyngrids/config/ap-dyn-grid-pager-config-base';
import {
  ApDynGridStatusColumnConfig
}                                                                                from '../../ap-dyngrids/config/ap-dyn-grid-status-column-config';
import {
  ApDynGridEditColumnConfig
}                                                                                from '../../ap-dyngrids/config/ap-dyn-grid-edit-column-config';
import {
  ApDynGridPagerConfig,
  IDynGridWizardChoiceData
}                                                                                from '../../ap-dyngrids/config/ap-dyn-grid-pager-config';
import {
  ApDynGridPropertyColumnConfig
}                                                                                from '../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {
  CropTypeStore
}                                                                                from '../../stores/base-data/crop.types.store';
import {
  PpProductWizardComponent
}                                                                                from '../../entry-components/plant-protection-entry-components/pp-product-wizard/pp-product-wizard.component';
import {filter, map}                                                             from 'rxjs/operators';
import {
  MapViewStore
}                                                                                from '../../stores/layout/mapview.store';
import {
  ApDateService
}                                                                                from '../../ap-core/services/ap-date-service';
import {ObjectFactory}                                                           from 'ts-tooling';
import {
  PlantProtectionProductStore
}                                                                                from '../../stores/base-data/plantprotection.product.store';
import {LoginStore}                                                              from '../../stores/login/login.store';
import {
  CampaignYearStore
}                                                                                from '../../stores/login/campaignyear.store';
import {
  ApTranslationService
}                                                                                from '../../ap-utils/service/ap-translation.service';
import {
  ApDynGridGroupColumnConfig
}                                                                                from '../../ap-dyngrids/config/ap-dyn-grid-group-column-config';
import {
  ApRoleTypeService
}                                                                                from '../../services/common/ap-role-type.service';
import {
  LanguageStore
}                                                                                from '../../stores/translation/language.store';
import {nameOf}                                                                  from '../../ap-core/utils/ap-name-of';
import {
  ApUtilService
}                                                                                from '../../ap-utils/service/ap-util.service';
import {
  IGridStatus
}                                                                                from '../../ap-interface/interfaces/ap-grid-status.interface';
import {FormStore}                                                               from '../../stores/layout/form.store';
import {
  CountryStore
}                                                                                from '../../stores/common/country.store';
import {
  ApColumnState
}                                                                                from '../../ap-interface/enums/ap-column-state.enum';
import {
  IPlantProtectionProductsGridData,
  PlantProtectionGridActions
}                                                                                from '../types/plant-protection-products.types';
import IPlantProtectionProduct = Data.BaseData.IPlantProtectionProduct;

/**
 * Component for plant protection products management
 */
@Component({
  selector: 'ap-plant-protection-products',
  template: `
    <ap-dyncomponent [caption]="'Global__PlantProtection'"
                     [canSearch]="true"
                     [canCreate]="(loading$ | async) === false"
                     (create)="createNewProduct()"
                     [headerIcon]="'ap-basedata-icon'"
                     [filterTooltip]="'filterTooltip'"
                     [columns]="columns"
                     [items]="this.items$ | async"
                     [loading$]="loading$"
                     [pager]="pager"
                     [groupable]="true"
                     [selectable]="{checkboxOnly: false, mode: 'multiple'}"
    ></ap-dyncomponent>`
})

export class ApPlantProtectionProductsComponent implements OnInit, AfterContentInit, OnDestroy {
  @ViewChild(ApDynComponentComponent, {static: true}) dynComponent: ApDynComponentComponent;
  public columns: ApDynGridColumnConfigBase[] = [];
  public pager: ApDynGridPagerConfigBase;
  public loading$ = of(true);
  public items$: Observable<IPlantProtectionProductsGridData[]> = of([]);

  private _applyClicked = new EventEmitter<any>();
  private _subscriptions: Array<Subscription> = [];

  constructor(private loginStore: LoginStore,
              private plantProtectionProductStore: PlantProtectionProductStore,
              private translationService: ApTranslationService,
              private mapViewStore: MapViewStore,
              private cropTypeStore: CropTypeStore,
              private campaignYearStore: CampaignYearStore,
              private languageStore: LanguageStore,
              private dateService: ApDateService,
              private formStore: FormStore,
              private countryStore: CountryStore) {
  }

  /**
   * Initializes the component
   * Hides the map view when the component is initialized
   */
  public ngOnInit(): void {
    this.mapViewStore.hideMapView();
  }

  /**
   * Performs initialization after content is initialized
   * Sets up subscriptions for campaign year changes and data loading
   * Triggers initial data retrieval and grid configuration
   */
  public ngAfterContentInit(): void {
    this._subscriptions.push(
      this._applyClicked.subscribe((e) => this.onApplyClicked(e))
    );
    this._subscriptions.push(
      this.campaignYearStore.Listen(s => s.selectedYear).subscribe(() => {
        this.plantProtectionProductStore.loadPlantProtectionProducts(false, true);
      })
    );
    this.getDataFromStore();
    this.gridBuilder();
  }

  /**
   * Cleans up subscriptions when the component is destroyed
   * Prevents memory leaks by unsubscribing from all active subscriptions
   */
  public ngOnDestroy(): void {
    this._subscriptions.forEach(x => x.unsubscribe());
  }

  /**
   * Opens the wizard to create a new plant protection product
   * Uses FormStore to open the product wizard component
   */
  public createNewProduct(): void {
    this.formStore.openForm({component: PpProductWizardComponent, data: null});
  }

  /**
   * Retrieves data from various stores
   * Sets up loading state and items observable
   * Transforms raw data into grid-compatible format
   * @private
   */
  private getDataFromStore(): void {
    this.countryStore.loadAllCountries();
    this.loading$ = combineLatest([
      this.plantProtectionProductStore.Listen(s => s.loading),
      this.plantProtectionProductStore.Listen(s => s.saving),
      this.campaignYearStore.Listen(s => s.loading),
      this.cropTypeStore.Listen(s => s.loading),
      this.countryStore.Listen(s => s.loading)
    ]).pipe(
      map(([plantProtectionProductsLoading, campaignYearLoading, croptypesLoading]) =>
          plantProtectionProductsLoading || campaignYearLoading || croptypesLoading)
    );
    this.items$ = combineLatest([
      this.plantProtectionProductStore.Listen(s => s.data),
      this.cropTypeStore.Listen(s => s.data)])
      .pipe(
        filter(([plantProtectionProducts, cropTypes]) => !!plantProtectionProducts && !!cropTypes),
        map(([plantProtectionProducts, _]) =>
          plantProtectionProducts.Convert<IPlantProtectionProductsGridData>((plantProtectionProduct: IPlantProtectionProduct) => {
            const useByDate = plantProtectionProduct.UseByDate ? this.dateService.getDateMidnight(plantProtectionProduct.UseByDate) : null;
            const permissionEndDate = plantProtectionProduct.PermissionEnd ? this.dateService.getDateMidnight(plantProtectionProduct.PermissionEnd) : null;
            const statusSelection = this.getSelectionStatus(plantProtectionProduct.Selectable);
            const statusProduct = this.getProductStatus(useByDate, permissionEndDate);
            const distinctEffRanges = plantProtectionProduct.ProductDetails?.map(d =>
              this.translationService.translate(d.EffectiveRange?.Key))?.filter((value, i, array) => array.indexOf(value) === i)?.join(', ');
            const productEppoCodes = plantProtectionProduct.ProductDetails?.map(d => d.EppoCrop);
            // Get all crops which are assigned to the product by its EppoCode.
            // Products may contain a certain crop eppoCode or an eppo_group
            // Filter empty crops and duplicates
            const targetCrops = this.cropTypeStore.CropTypes.FindAll(c =>
              c.Selected &&
              (productEppoCodes.Contains(c.Eppocode) || c.EppoGroups?.Any(g => productEppoCodes.Contains(g))))?.map(c => this.translationService.translate(c.Description))?.filter((value, index, self) => self.indexOf(value) === index && value !== '')?.join(', ');
            return {
              Id: plantProtectionProduct.Id,
              ProductName: plantProtectionProduct.ProductName,
              StatusSelection: statusSelection,
              StatusProduct: statusProduct,
              Ingredients: this.parseIngredients(plantProtectionProduct),
              StartDate: plantProtectionProduct.PermissionStart ? this.dateService.getDateMidnight(plantProtectionProduct.PermissionStart) : null,
              EndDate: plantProtectionProduct.PermissionEnd ? this.dateService.getDateMidnight(plantProtectionProduct.PermissionEnd) : null,
              UseByDate: useByDate,
              EffRange: distinctEffRanges,
              Crops: targetCrops,
              DetailsTooltip: this.buildDetailsTooltip(plantProtectionProduct),
              IsSystemProduct: plantProtectionProduct?.FarmId === -1,
              SourceItem: plantProtectionProduct
            };
          })
        )
      );
  }

  /**
   * Configures grid columns and pager
   * Sets up column definitions, filtering, sorting, and mass edit options
   * @private
   */
  private gridBuilder(): void {
    this.columns = [
      new ApDynGridStatusColumnConfig({
        field: `${nameOf<IPlantProtectionProductsGridData>('StatusSelection')}.${nameOf<IGridStatus>('state')}`,
        class: `${nameOf<IPlantProtectionProductsGridData>('StatusSelection')}.${nameOf<IGridStatus>('class')}`,
        description: `${nameOf<IPlantProtectionProductsGridData>('StatusSelection')}.${nameOf<IGridStatus>('description')}`,
        title: 'Global__Status',
        showHeaderTitle: false,
        filterable: true,
        groupIndex: 0,
        groupDesc: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: nameOf<IPlantProtectionProductsGridData>('EffRange'),
        title: 'Global__Effective_Range',
        filterable: true,
        width: 80,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: nameOf<IPlantProtectionProductsGridData>('ProductName'),
        title: 'Global__Name',
        filterable: true,
        width: 100,
        headerFilterable: true,
        sortIndex: 0,
        sortDesc: false,
        tooltip: (dataItem) => dataItem['DetailsTooltip']
      }),
      new ApDynGridPropertyColumnConfig({
        field: nameOf<IPlantProtectionProductsGridData>('Ingredients'),
        title: 'Ingredients',
        width: 275,
        hide: true,
        filterable: true,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: nameOf<IPlantProtectionProductsGridData>('Crops'),
        title: 'Docu_Ini__CropTypes',
        width: 275,
        filterable: true,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: nameOf<IPlantProtectionProductsGridData>('IsSystemProduct'),
        title: 'System_Product',
        checkbox: true,
        filterable: true,
        width: 60
      }),
      new ApDynGridGroupColumnConfig({
        title: 'Global__Admission',
        groupColumns: [
          new ApDynGridStatusColumnConfig({
            field: `${nameOf<IPlantProtectionProductsGridData>('StatusProduct')}.${nameOf<IGridStatus>('state')}`,
            class: `${nameOf<IPlantProtectionProductsGridData>('StatusProduct')}.${nameOf<IGridStatus>('class')}`,
            description: `${nameOf<IPlantProtectionProductsGridData>('StatusProduct')}.${nameOf<IGridStatus>('description')}`,
            title: 'Global__Admission',
            showHeaderTitle: false,
            groupDesc: true,
            filterable: true
          }),
          new ApDynGridPropertyColumnConfig({
            field: nameOf<IPlantProtectionProductsGridData>('EndDate'),
            title: 'Docu_Ini__EndDate',
            filterable: true,
            width: 80,
            headerFilterable: true,
            hide: true,
            format: this.languageStore.CurrentDateFormat
          }),
          new ApDynGridPropertyColumnConfig({
            field: nameOf<IPlantProtectionProductsGridData>('UseByDate'),
            title: 'Global__Expire_Date',
            filterable: true,
            width: 80,
            headerFilterable: true,
            hide: true,
            format: this.languageStore.CurrentDateFormat
          })
        ]
      }),
      new ApDynGridEditColumnConfig({
        data: [{
          callback: ApUtilService.createEventEmitter(this.onStatusToggle.bind(this), this._subscriptions),
          text: (dataItem: IPlantProtectionProductsGridData) => dataItem.SourceItem.Selectable
            ? 'Global__Disable'
            : 'Global__Enable',
          disabled: false
        }]
      })
    ];
    this.pager = new ApDynGridPagerConfig({
      listMassEditing: [
        {
          id: PlantProtectionGridActions.Active,
          name: this.translationService.translate('Global__Enable'),
          type: ApGridPagerType.None,
          values: null
        } as IDynGridWizardChoiceData,
        {
          id: PlantProtectionGridActions.Inactive,
          name: this.translationService.translate('Global__Disable'),
          type: ApGridPagerType.None,
          values: null
        } as IDynGridWizardChoiceData
      ],
      apply: this._applyClicked,
      hasPermission: true
    });
  }

  /**
   * Handles mass edit actions for plant protection products
   * Updates the selectability of multiple products
   * @param data The selected mass edit action data
   * @private
   */
  private onApplyClicked(data: IGridPagerData): void {
    const ppProducts: IPlantProtectionProduct[] = [];
    const rows = this.dynComponent.getSelectedItems() as IPlantProtectionProductsGridData[];
    for (const row of rows) {
      const item = ObjectFactory.Copy(row.SourceItem);
      if (item) {
        item.Selectable = data.object.id === PlantProtectionGridActions.Active;
        ppProducts.push(item);
      }
    }
    if (ppProducts?.length > 0) {
      this.plantProtectionProductStore.updateSelectablePlantProtectionProduct(ppProducts);
    }
  }

  /**
   * Toggles the selectable status of a single plant protection product
   * @param item The grid item whose status is to be changed
   * @private
   */
  private onStatusToggle(item: IPlantProtectionProductsGridData): void {
    const copiedPlantProtectionProduct = ObjectFactory.Copy(item.SourceItem);
    if (copiedPlantProtectionProduct) {
      copiedPlantProtectionProduct.Selectable = !copiedPlantProtectionProduct.Selectable;
      this.plantProtectionProductStore.updateSelectablePlantProtectionProduct([copiedPlantProtectionProduct]);
    }
  }

  /**
   * Determines the selection status of a product
   * @param productSelected Boolean indicating if the product is selected
   * @returns Grid status object with state, class, and description
   * @private
   */
  private getSelectionStatus(productSelected: boolean): IGridStatus {
    if (productSelected === true) {
      return {
        state: ApColumnState.Green,
        class: 'ap-status-green',
        description: 'Global__Active'
      };
    }
    return {
      state: ApColumnState.Red,
      class: 'ap-status-red',
      description: 'Global__Inactive'
    };
  }

  /**
   * Determines the product status based on use-by and permission end dates
   * @param useByDate The date until the product can be used
   * @param permissionEndDate The date when product permission expires
   * @returns Grid status object with state, class, and description
   * @private
   */
  private getProductStatus(useByDate: Date, permissionEndDate: Date): IGridStatus {
    const now = new Date();
    if (permissionEndDate > now && useByDate > now) {
      return {
        state: ApColumnState.Green,
        class: 'ap-status-green',
        description: 'Admin_Pages_Booking__Valid'
      };
    } else if (permissionEndDate < now && useByDate >= now) {
      return {
        state: ApColumnState.Yellow,
        class: 'ap-status-yellow',
        description: `${this.translationService.translate('Admin_Pages_Booking__Valid')} (${this.translationService.translate('End_of_admission')})`
      };
    }
    return {
      state: ApColumnState.Red,
      class: 'ap-status-red',
      description: 'AgriConnect_Refused'
    };
  }

  /**
   * Parses ingredients from a plant protection product
   * @param plantProtectionProduct The product to parse ingredients from
   * @returns Comma-separated string of ingredients or undefined
   * @private
   */
  private parseIngredients(plantProtectionProduct: IPlantProtectionProduct): string | undefined {
    if (!plantProtectionProduct.Ingredients) {
      return undefined;
    }
    const parsedIngredients = JSON.parse(plantProtectionProduct.Ingredients) as string[];
    if (!Array.isArray(parsedIngredients) || parsedIngredients.length === 0) {
      return undefined;
    }
    return parsedIngredients.join('; ');
  }

  /**
   * ONLY for debugMode: builds an additional product tooltip with product's details of application
   * @param plantProtectionProduct
   * @private
   */
  private buildDetailsTooltip(plantProtectionProduct: IPlantProtectionProduct): string {
    if (!plantProtectionProduct?.ProductDetails || plantProtectionProduct?.ProductDetails?.length <= 0 || !ApRoleTypeService.hasAgriconRole(this.loginStore.User)) {
      return '';
    }
    let tooltip = `<table style="width: 800px; border-collapse: collapse; height: 50px; border: 1px solid black;"> ` +
      `<caption>${plantProtectionProduct?.ProductName}</caption>\n<tbody>\n` +
      `<tr style="height: 20px;">\n` +
      `<td style="width: 60px; height: 18px; text-align: center; border: 1px solid black; word-break: break-word">${this.translationService.translate('Global__EC_Min')}</td>\n` +
      `<td style="width: 60px; height: 18px; text-align: center; border: 1px solid black; word-break: break-word">${this.translationService.translate('Global__EC_Max')}</td>\n` +
      `<td style="width: 60px; height: 18px; text-align: center; border: 1px solid black; word-break: break-word">${this.translationService.translate('Global__Max_Admission')}</td>\n` +
      `<td style="width: 60px; height: 18px; text-align: center; border: 1px solid black; word-break: break-word">${this.translationService.translate('Settings__Lbl_Menu_Unit')}</td>\n` +
      `<td style="width: 60px; height: 18px; text-align: center; border: 1px solid black; word-break: break-word">${this.translationService.translate('Global__Eppo_Crop')}</td>\n` +
      `<td style="width: 60px; height: 18px; text-align: center; border: 1px solid black; word-break: break-word">${this.translationService.translate('Global__Eppo_Harmful_Organism')}</td>\n` +
      `<td style="width: 60px; height: 18px; text-align: center; border: 1px solid black; word-break: break-word">${this.translationService.translate('Global__Effective_Range')}</td>\n` +
      `<td style="width: 120px; height: 18px; text-align: center; border: 1px solid black; word-break: break-word">${this.translationService.translate('Docu_Ini__WorkType')}</td>\n` +
      `<td style="width: 120px; height: 18px; text-align: center; border: 1px solid black; word-break: break-word">${this.translationService.translate('Base_Pages_Rax__IndicationNote')}</td>\n` +
      `</tr>\n`;
    for (const detail of plantProtectionProduct.ProductDetails) {
      tooltip += '<tr style="height: 18px;">\n' +
        `<td style="width: 60px; height: 18px; text-align: center; border: 1px solid black; white-space: nowrap">${detail.EcMin}</td>\n` +
        `<td style="width: 60px; height: 18px; text-align: center; border: 1px solid black; white-space: nowrap">${detail.EcMax}</td>\n` +
        `<td style="width: 60px; height: 18px; text-align: center; border: 1px solid black; white-space: nowrap">${detail.AmountMax}</td>\n` +
        `<td style="width: 60px; height: 18px; text-align: center; border: 1px solid black; white-space: nowrap">${this.translationService.translate(detail.Unit?.Key)}</td>\n` +
        `<td style="width: 60px; height: 18px; text-align: center; border: 1px solid black; white-space: nowrap">${detail.EppoCrop ? detail.EppoCrop : ''}</td>\n` +
        `<td style="width: 60px; height: 18px; text-align: center; border: 1px solid black; white-space: nowrap">${detail.EppoHarmfulOrganism ? detail.EppoHarmfulOrganism : ''}</td>\n` +
        `<td style="width: 60px; height: 18px; text-align: center; border: 1px solid black; white-space: nowrap">${this.translationService.translate(detail.EffectiveRange?.Key)}</td>\n` +
        `<td style="width: 120px; height: 18px; text-align: center; border: 1px solid black; white-space: nowrap">${this.translationService.translate(detail?.WorkType?.Description)}</td>\n` +
        `<td style="width: 120px; height: 18px; text-align: center; border: 1px solid black; white-space: nowrap">${detail?.Indicator ? (detail.Indicator?.length > 200 ? detail.Indicator.slice(0, 200) : detail.Indicator) : ''}</td>\n` +
        '</tr>\n';
    }
    tooltip += '</tbody>\n</table>';
    return tooltip;
  }
}
